// Branch-aware product pricing.
//
// Schema (on each Firestore product document, optional):
//   prices: { limpopo: number|null, pretoria: number|null }
//   priceHistory: [{ price, branch, changedAt, changedBy }]
//
// `unitPrice` (legacy, flat number) is retained for backward compatibility
// and consulted as a fallback when `prices[branch]` is missing.
// `null` inside `prices` means "explicitly not sold at this branch".
// Absence of a key means "fall back to unitPrice".

const DEPT_TO_BRANCH = {
  kitchen: 'limpopo',
  bakery: 'limpopo',
  tuckshop: 'limpopo',
  yawehs: 'pretoria',
}

/** 'limpopo' | 'pretoria' | null (null for unknown depts). */
export function getDepartmentBranch(dept) {
  if (!dept) return null
  return DEPT_TO_BRANCH[dept] || null
}

export const BRANCHES = ['limpopo', 'pretoria']

/** Return the price to charge for this product at the branch implied by
 *  `dept`. Order of resolution:
 *   1. prices[branch] if the key exists and is a number > 0 → that number
 *   2. prices[branch] === null (explicit "not sold here") → null
 *   3. Legacy unitPrice fallback → number (or 0 if not set)
 *
 *  Null return means the product is not available at the given branch;
 *  callers should skip / hide it rather than treat it as R0.
 */
export function getProductPrice(product, dept) {
  if (!product) return 0
  const branch = getDepartmentBranch(dept)
  if (!branch) return Number(product.unitPrice) || 0

  const prices = product.prices
  if (prices && branch in prices) {
    const v = prices[branch]
    if (v === null) return null
    const n = Number(v)
    if (Number.isFinite(n) && n > 0) return n
    // numeric 0 counts as explicit "not sold" under this helper, matching
    // the legacy behaviour where unitPrice === 0 meant no fixed price.
    if (n === 0) return null
  }

  return Number(product.unitPrice) || 0
}

/** True if this product exists for the given department's branch.
 *  A product with `prices[branch] === null` is unavailable; one with a
 *  numeric price (or only the legacy unitPrice) is available.
 *  Continuous-mirror products with unitPrice null are treated as
 *  available — they do have a branch presence, just no fixed price. */
export function isProductAvailableAt(product, dept) {
  if (!product) return false
  const branch = getDepartmentBranch(dept)
  if (!branch) return true

  const prices = product.prices
  if (prices && branch in prices) {
    return prices[branch] !== null
  }
  // No prices map yet (legacy doc) — fall back to department tag.
  if (product.department) {
    return getDepartmentBranch(product.department) === branch
  }
  return true
}

/** Filter a product list down to those that belong to the given
 *  department. Primary filter is `product.department === dept`.
 *  A product tagged to a different department still appears when it
 *  carries an explicit cross-branch price in its `prices` map
 *  (prices[branch] is numeric and non-null) — used to publish a single
 *  doc to both branches without duplicating it. */
export function getProductsForDepartment(products, dept) {
  if (!Array.isArray(products)) return []
  const branch = getDepartmentBranch(dept)
  return products.filter(p => {
    if (!p) return false
    if (p.department === dept) return isProductAvailableAt(p, dept)
    if (!p.department) return isProductAvailableAt(p, dept)
    // Cross-department: require an explicit numeric cross-branch price
    // AND the target branch must be the opposite of the product's own.
    if (!branch) return false
    const crossPrice = p.prices?.[branch]
    if (crossPrice == null) return false
    return Number(crossPrice) > 0
  })
}

/** One-off migration: fill in prices.{limpopo,pretoria} on products that
 *  don't have the map yet. Uses the product's existing department tag
 *  to decide which branch gets the legacy unitPrice and which gets null.
 *  Pure — returns a new list; does not write to Firestore. */
export function migrateProductPrices(products) {
  if (!Array.isArray(products)) return []
  return products.map(p => {
    if (p?.prices && ('limpopo' in p.prices) && ('pretoria' in p.prices)) {
      return p
    }
    const branch = getDepartmentBranch(p?.department)
    const unit = Number(p?.unitPrice)
    const numericPrice = Number.isFinite(unit) && unit > 0 ? unit : null
    const prices = { limpopo: null, pretoria: null, ...(p?.prices || {}) }
    if (branch === 'limpopo') prices.limpopo = numericPrice
    else if (branch === 'pretoria') prices.pretoria = numericPrice
    return { ...p, prices }
  })
}
